import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Phone, Mail, Linkedin } from "lucide-react";
import { API, api } from "@/lib/api";

const nav = [
  { label: "Home", to: "/#home" },
  { label: "About Us", to: "/#about" },
  { label: "Services", to: "/#services" },
  { label: "Industries", to: "/#industries" },
  { label: "Jobs", to: "/jobs" },
  { label: "Knowledge Hub", to: "/hub" },
  { label: "Contact Us", to: "/contact" },
];

export const Logo = ({ light = false }) => {
  const [ok, setOk] = useState(true);
  const v = typeof window !== "undefined" ? (localStorage.getItem("iws_logo_v") || "") : "";
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-2 select-none">
      {ok ? (
        <img src={`${API}/logo${v ? `?v=${v}` : ""}`} onError={() => setOk(false)} alt="Ishaan Workforce Solutions" className={`h-11 sm:h-12 w-auto max-w-[220px] object-contain ${light ? "bg-white rounded-md p-1.5" : ""}`} />
      ) : (
        <>
          <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg font-display font-bold text-lg ${light ? "bg-white text-navy" : "bg-navy text-white"}`}>I</span>
          <span className={`font-display font-semibold text-lg leading-tight ${light ? "text-white" : "text-navy"}`}>Ishaan<span className="text-emerald-brand">WFS</span></span>
        </>
      )}
    </Link>
  );
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => { api.get("/content").then((r) => setContent(r.data)).catch(() => {}); }, []);

  const c = content?.contact || {};
  const phone = (c.phones && c.phones[0]) || "+91 98765 43210";
  const email = c.email || "info@ishaanworkforcesolutions.com";
  const linkedin = c.linkedin || "https://www.linkedin.com/company/ishaan-workforce-solutions";
  const hireLabel = content?.hire_cta_label || "Hire Talent";
  const resumeLabel = content?.resume_cta_label || "Submit Resume";

  const goSection = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (loc.pathname !== "/") { navigate("/"); setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120); }
      else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else navigate(to);
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden md:block bg-navy text-slate-200 text-xs">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 hover:text-emerald-light transition-colors" data-testid="topbar-phone"><Phone size={13} /> {phone}</a>
            <a href={`mailto:${email}`} className="flex items-center gap-1.5 hover:text-emerald-light transition-colors" data-testid="topbar-email"><Mail size={13} /> {email}</a>
          </div>
          <a href={linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-emerald-light transition-colors" data-testid="topbar-linkedin"><Linkedin size={13} /> LinkedIn</a>
        </div>
      </div>
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7">
            {nav.map((n) => (
              <button key={n.label} onClick={() => goSection(n.to)} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-slate-600 hover:text-navy transition-colors">{n.label}</button>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/submit-resume" data-testid="header-submit-resume" className="text-sm font-medium text-navy border-2 border-navy rounded-full px-4 py-1.5 hover:bg-navy hover:text-white transition-colors">{resumeLabel}</Link>
            <Link to="/hire-talent" data-testid="header-hire-talent" className="text-sm font-semibold text-white bg-emerald-brand rounded-full px-5 py-2 hover:bg-emerald-dark transition-colors active:scale-95">{hireLabel}</Link>
          </div>
          <button className="lg:hidden text-navy" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-4 space-y-3">
            {nav.map((n) => (
              <button key={n.label} onClick={() => goSection(n.to)} className="block w-full text-left text-slate-700 font-medium py-1">{n.label}</button>
            ))}
            <div className="flex gap-3 pt-2">
              <Link to="/submit-resume" onClick={() => setOpen(false)} className="flex-1 text-center text-sm font-medium text-navy border-2 border-navy rounded-full px-4 py-2">{resumeLabel}</Link>
              <Link to="/hire-talent" onClick={() => setOpen(false)} className="flex-1 text-center text-sm font-semibold text-white bg-emerald-brand rounded-full px-4 py-2">{hireLabel}</Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
